import {type BreakpointSize, Button, Grid, Heading, Image, Link, Text, useWindowSize} from '@primer/react-brand'
import {Spacer} from './_shared'
import SectionIntro from './components/SectionIntro/SectionIntro'
import {Fragment, useCallback, useMemo, useRef, useState} from 'react'
import {COPY, CUSTOMER_STORIES, StoryCategory} from './CustomerStories.data'
import Toggle from './components/Toggle/Toggle'
import {wait} from './utils/time'

export default function CustomerStories() {
  const [currentCategory, setCurrentCategory] = useState<StoryCategory>(StoryCategory.Industry)
  const gridWrapperRef = useRef<HTMLDivElement>(null)
  const windowSize = useWindowSize()

  const currentStories = useMemo(
    () =>
      Object.values(CUSTOMER_STORIES[currentCategory])
        .map(subCategories => {
          return Object.values(subCategories).sort((a, b) => Number(a.subCategory) - Number(b.subCategory))
        })
        .flat(),
    [currentCategory],
  )

  const onCategoryChange = useCallback(
    async (category: StoryCategory) => {
      if (!gridWrapperRef.current || category === currentCategory) return

      const grid = gridWrapperRef.current.querySelector('.lp-CustomerStories-grid')
      if (!grid) return

      grid.classList.add('lp-CustomerStories-grid--hidden')
      await wait(200)

      setCurrentCategory(category)
      await wait(200)

      grid.classList.remove('lp-CustomerStories-grid--hidden')
    },
    [currentCategory],
  )

  return (
    <section id="customer-stories" className="lp-Section">
      <div className="lp-SectionBlock">
        <div className="lp-SectionBlock-content">
          <SectionIntro mascot="ducky" title={COPY.intro.title} bottomSpacerSize="small" />

          {/* TODO: See if sticky can be implemented */}
          <Toggle
            items={Object.entries(COPY.content.categories).map(([category, label]) => ({label, value: category}))}
            ariaLabel={COPY.content.aria.toggle}
            onToggle={(value: string) => onCategoryChange(value as StoryCategory)}
          />

          <Spacer size="32px" size1012="56px" />
        </div>
      </div>

      <div ref={gridWrapperRef} className="lp-SectionBlock">
        <div className="lp-SectionBlock-content lp-SectionBlock-content--lines">
          <Grid className="lp-CustomerStories-grid">
            {currentStories.map((story, index) => (
              <Grid.Column
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`story_${index}`}
                className="lp-CustomerStories-gridColumn"
                span={{xsmall: 12, medium: 6, large: 4}}
              >
                <Button className="lp-CustomerStories-button" as="a" href={story.url} hasArrow={false}>
                  <Image
                    className="lp-CustomerStories-logo"
                    src={story.logo.url}
                    alt={story.logo.alt}
                    style={{height: story.logo.height}}
                  />
                  <div>
                    <Heading className="lp-CustomerStories-heading" size="subhead-medium">
                      {COPY.content.subCategories[story.subCategory]}
                    </Heading>
                    <Text className="lp-CustomerStories-description">{story.description}</Text>

                    <Link variant="accent" className="lp-CustomerStories-storyLink">
                      <Text weight="normal">{COPY.content.readMore}</Text>
                    </Link>
                  </div>
                </Button>
              </Grid.Column>
            ))}
            {/* Add empty column to fill the grid if needed */}
            {((windowSize.currentBreakpointSize === 'medium' && currentStories.length % 2 !== 0) ||
              (['large', 'xlarge', 'xxlarge'].includes(windowSize.currentBreakpointSize as BreakpointSize) &&
                currentStories.length % 3 !== 0)) && (
              <Grid.Column
                className="lp-CustomerStories-gridColumn lp-CustomerStories-gridColumn--dummy"
                span={{xsmall: 12, medium: 6, large: 4}}
              />
            )}
          </Grid>
        </div>
      </div>

      <div className="lp-SectionBlock lp-SectionBlock--noBorder">
        <div className="lp-SectionBlock-content">
          <Spacer size="0px" size1012="48px" />

          {/* Links */}
          <div className="lp-CustomerStories-links">
            {COPY.links.map((link, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <Fragment key={`link_${index}`}>
                <div>
                  <Link variant="accent" className="lp-CustomerStories-link" href={link.url}>
                    <Text weight="normal">{link.label}</Text>
                  </Link>
                </div>
                {index < COPY.links.length - 1 && <hr className="lp-CustomerStories-divider" />}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

try{ CustomerStories.displayName ||= 'CustomerStories' } catch {}